/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AddClientCreditDto } from '../models/AddClientCreditDto';
import type { AdviserLinkResponseDto } from '../models/AdviserLinkResponseDto';
import type { ClientResponseDto } from '../models/ClientResponseDto';
import type { CreateAdviserLinkDto } from '../models/CreateAdviserLinkDto';
import type { CreateClientDto } from '../models/CreateClientDto';
import type { EntityActivityDto } from '../models/EntityActivityDto';
import type { InvalidateDocumentCacheResponseDto } from '../models/InvalidateDocumentCacheResponseDto';
import type { PaginatedAndoApiLogsResponseDto } from '../models/PaginatedAndoApiLogsResponseDto';
import type { PaginatedClientSearchResponseDto } from '../models/PaginatedClientSearchResponseDto';
import type { UpdateClientDto } from '../models/UpdateClientDto';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class ClientsService {
    /**
     * Create a new client
     *
     * Creates a new client in the database.
     *
     * **Authorization Required:**
     * - Requires JWT authentication token
     * - No role restrictions for client creation
     *
     * **Client Types:**
     * - INDIVIDUAL: Personal client (uses firstName, lastName, dob, phone, email as main contact)
     * - BUSINESS: Business client (uses firstName for business name, lastName can be empty; can include mainContact for the person representing the business)
     *
     * **Notes:**
     * - assignedAdviserId is optional; if provided, must reference an existing adviser
     * - For BUSINESS clients, mainContact is optional but recommended
     * - Business-specific fields (companyRegNo, tradingAs, gstNumber) are only used for BUSINESS clients
     *
     * @param requestBody Client creation data
     * @returns ClientResponseDto Client created successfully
     * @throws ApiError
     */
    public static clientsControllerCreate(
        requestBody: CreateClientDto,
    ): CancelablePromise<ClientResponseDto> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/clients',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request - invalid input data or adviser not found (if assignedAdviserId provided)`,
                401: `Unauthorized - JWT token missing or invalid`,
            },
        });
    }
    /**
     * List all clients
     *
     * Retrieves a paginated list of clients with optional filtering and sorting.
     *
     * **Filtering Options:**
     * - search: Filter by client/person name, business name, company reg no, or email (case-insensitive, multi-word supported)
     * - clientType: Filter by INDIVIDUAL or BUSINESS
     * - status: Filter by client status (IN_PROGRESS, BOUND, LAPSED, RENEWAL_DUE)
     * - assignedAdviserId: Filter by assigned adviser
     * - quotePolicyNumber: Filter by quote ID or policy ID (partial match, case-insensitive)
     *
     * **Sorting:**
     * - sortBy: Field to sort by (firstName, lastName, email, createdAt, updatedAt, status, lastActivity)
     * - sortOrder: asc or desc
     *
     * **Pagination:**
     * - page: Page number (1-based, default: 1)
     * - limit: Items per page (1-100, default: 10)
     *
     * @param page Page number (1-based)
     * @param limit Number of items per page
     * @param search Search term to filter by client/person name, business name (tradingAs), company reg no, or email (case-insensitive). Multi-word terms are tokenized.
     * @param clientType Filter by client type
     * @param status Filter by client status
     * @param assignedAdviserId Filter by assigned adviser ID
     * @param quotePolicyNumber Search term to filter by quote ID or policy ID (partial match, case-insensitive)
     * @param sortBy Sort field
     * @param sortOrder Sort order
     * @returns PaginatedClientSearchResponseDto List of clients with pagination metadata
     * @throws ApiError
     */
    public static clientsControllerFindAll(
        page: number = 1,
        limit: number = 10,
        search?: string,
        clientType?: 'INDIVIDUAL' | 'BUSINESS',
        status?: 'IN_PROGRESS' | 'BOUND' | 'LAPSED' | 'RENEWAL_DUE',
        assignedAdviserId?: string,
        quotePolicyNumber?: string,
        sortBy: 'firstName' | 'lastName' | 'email' | 'createdAt' | 'updatedAt' | 'status' | 'lastActivity' = 'createdAt',
        sortOrder: 'asc' | 'desc' = 'desc',
    ): CancelablePromise<PaginatedClientSearchResponseDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/clients',
            query: {
                'page': page,
                'limit': limit,
                'search': search,
                'clientType': clientType,
                'status': status,
                'assignedAdviserId': assignedAdviserId,
                'quotePolicyNumber': quotePolicyNumber,
                'sortBy': sortBy,
                'sortOrder': sortOrder,
            },
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
            },
        });
    }
    /**
     * Get a client by ID
     *
     * Retrieves a single client by their UUID.
     *
     * **Includes:**
     * - Client details
     * - Assigned adviser information
     * - Main contact (for BUSINESS clients)
     *
     * @param id Client UUID
     * @returns ClientResponseDto Client found
     * @throws ApiError
     */
    public static clientsControllerFindOne(
        id: string,
    ): CancelablePromise<ClientResponseDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/clients/{id}',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
                404: `Client not found`,
            },
        });
    }
    /**
     * Update a client
     *
     * Updates an existing client with the provided data.
     *
     * **Notes:**
     * - Only provided fields are updated (partial update)
     * - assignedAdviserId is optional; if provided, must reference an existing adviser
     * - For BUSINESS clients, mainContact can be created or updated
     * - Updates the lastActivity timestamp automatically
     * - If the client has an Ando customer ID, an asynchronous push to Ando runs after a successful save (poll GET /clients/:id for andoCustomerSyncedAt / andoCustomerSyncError)
     *
     * @param id Client UUID
     * @param requestBody Client update data
     * @returns ClientResponseDto Client updated successfully
     * @throws ApiError
     */
    public static clientsControllerUpdate(
        id: string,
        requestBody: UpdateClientDto,
    ): CancelablePromise<ClientResponseDto> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/v1/clients/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request - invalid input data or adviser not found`,
                401: `Unauthorized - JWT token missing or invalid`,
                404: `Client not found`,
            },
        });
    }
    /**
     * Delete a client
     *
     * Soft-deletes a client by setting the deletedAt timestamp.
     *
     * **Notes:**
     * - The client is not permanently deleted
     * - Soft-deleted clients are excluded from list queries
     * - Related data (addresses, additional insureds, etc.) is preserved
     *
     * @param id Client UUID
     * @returns void
     * @throws ApiError
     */
    public static clientsControllerRemove(
        id: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/v1/clients/{id}',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
                404: `Client not found`,
            },
        });
    }
    /**
     * Refresh client unallocated cash
     * Recalculates unallocated cash from credit invoices with negative outstanding balances and updates the client record.
     * @param id Client UUID
     * @returns ClientResponseDto Client unallocated cash refreshed successfully
     * @throws ApiError
     */
    public static clientsControllerRefreshUnallocatedCash(
        id: string,
    ): CancelablePromise<ClientResponseDto> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/clients/{id}/refresh-unallocated-cash',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
                404: `Client not found`,
            },
        });
    }
    /**
     * Add manual client credit
     * Adds invoice-free credit to a client account. Only finance admin and super admin can add credits.
     * @param id Client UUID
     * @param requestBody
     * @returns ClientResponseDto Manual credit added successfully
     * @throws ApiError
     */
    public static clientsControllerAddClientCredit(
        id: string,
        requestBody: AddClientCreditDto,
    ): CancelablePromise<ClientResponseDto> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/clients/{id}/credits',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `Forbidden - insufficient role`,
                404: `Client not found`,
            },
        });
    }
    /**
     * List manual client credit activities
     * Returns credit-added activity history for the client profile.
     * @param id Client UUID
     * @returns EntityActivityDto Client credit activities
     * @throws ApiError
     */
    public static clientsControllerGetClientCreditActivities(
        id: string,
    ): CancelablePromise<Array<EntityActivityDto>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/clients/{id}/credit-activities',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
                404: `Client not found`,
            },
        });
    }
    /**
     * Sync client to Ando
     * Triggers async Ando customer sync for an existing client and immediately returns current client state. Poll GET /clients/:id for completion.
     * @param id Client UUID
     * @param force Force resync even if sync previously failed. Clears previous error state. For clients already in Ando, re-pushes the current details (update).
     * @returns ClientResponseDto Sync accepted; returns current client state. andoCustomerId is populated once async sync completes.
     * @throws ApiError
     */
    public static clientsControllerSyncAndo(
        id: string,
        force?: boolean,
    ): CancelablePromise<ClientResponseDto> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/clients/{id}/sync-ando',
            path: {
                'id': id,
            },
            query: {
                'force': force,
            },
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
                404: `Client not found`,
            },
        });
    }
    /**
     * Invalidate cached documents for all of a client's policies and quotes
     *
     * Clears the cached documents for every policy and quote belonging to the client.
     *
     * For each policy/quote it soft-deletes the associated Document rows and removes the
     * underlying S3 objects. The next download of a schedule/quote/invoice regenerates
     * a fresh copy.
     *
     * **Authorization Required:**
     * - Requires JWT authentication token
     * - Requires SUPER_ADMIN role
     *
     * @param id Client UUID
     * @returns InvalidateDocumentCacheResponseDto Document cache invalidated successfully
     * @throws ApiError
     */
    public static clientsControllerInvalidateDocumentCache(
        id: string,
    ): CancelablePromise<InvalidateDocumentCacheResponseDto> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/clients/{id}/invalidate-document-cache',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
                403: `Forbidden - user does not have required role (SUPER_ADMIN)`,
                404: `Client not found`,
            },
        });
    }
    /**
     * Get Ando API logs for a client
     *
     * Retrieves paginated Ando API call logs for the given client.
     *
     * **Authorization Required:**
     *
     * - Requires JWT authentication token
     *
     * - Requires SUPER_ADMIN role
     *
     *
     * @param id
     * @param page
     * @param limit
     * @param operation Filter logs by operation name (case-insensitive partial match)
     * @param status Filter logs by outcome (SUCCESS or ERROR)
     * @returns PaginatedAndoApiLogsResponseDto Paginated Ando API logs for the client
     * @throws ApiError
     */
    public static clientsControllerGetAndoLogsForClient(
        id: string,
        page: string,
        limit: string,
        operation?: string,
        status?: 'SUCCESS' | 'ERROR',
    ): CancelablePromise<PaginatedAndoApiLogsResponseDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/clients/{id}/ando-logs',
            path: {
                'id': id,
            },
            query: {
                'page': page,
                'limit': limit,
                'operation': operation,
                'status': status,
            },
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
                403: `Forbidden - user does not have required role (SUPER_ADMIN)`,
            },
        });
    }
    /**
     * Create an adviser link for a client
     *
     * Creates a new adviser link (Authorised Rep or Servicing Adviser) for a client.
     *
     * **Behavior:**
     * - If there's an existing CURRENT link of the same type, it will be marked as HISTORICAL
     * - The existing link's effectiveTo will be set to the day before the new link's effectiveFrom
     * - The new link will be created with status CURRENT
     *
     * **Authorization Required:**
     * - Requires JWT authentication token
     *
     * **Notes:**
     * - adviserId must reference an existing adviser
     * - fapId must reference an existing FAP
     * - effectiveFrom is optional (defaults to today's date in NZT when omitted)
     * - effectiveTo is optional (leave blank for ongoing assignments)
     * - commissionRecipient determines where commissions are allocated
     *
     * @param id Client UUID
     * @param requestBody Adviser link creation data
     * @returns AdviserLinkResponseDto Adviser link created successfully
     * @throws ApiError
     */
    public static clientsControllerCreateAdviserLink(
        id: string,
        requestBody: CreateAdviserLinkDto,
    ): CancelablePromise<AdviserLinkResponseDto> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/clients/{id}/adviser-links',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request - invalid input data, client/adviser/FAP not found, or validation error`,
                401: `Unauthorized - JWT token missing or invalid`,
                404: `Client not found`,
            },
        });
    }
    /**
     * Remove the current adviser link of a given type for a client
     *
     * Removes the current adviser link (e.g. AUTHORISED_REP) for a client by marking it as HISTORICAL with effectiveTo set to today.
     *
     * **Authorization Required:**
     * - Requires JWT authentication token
     * - Quoting advisers cannot remove the AUTHORISED_REP link
     *
     * @param id Client UUID
     * @param linkType Adviser link type
     * @returns void
     * @throws ApiError
     */
    public static clientsControllerRemoveAdviserLink(
        id: string,
        linkType: 'AUTHORISED_REP' | 'SERVICING_ADVISER',
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/v1/clients/{id}/adviser-links/{linkType}',
            path: {
                'id': id,
                'linkType': linkType,
            },
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
                403: `Forbidden - insufficient permissions`,
                404: `Client or current adviser link not found`,
            },
        });
    }
}
